import { apiFetch } from "./client.ts";

// Quotes
export async function listQuotes(token: string | null, params?: { clientId?: string; status?: string; search?: string; limit?: number }) {
  const q = new URLSearchParams();
  if (params?.clientId) q.set("clientId", params.clientId);
  if (params?.status) q.set("status", params.status);
  if (params?.search) q.set("search", params.search);
  if (params?.limit) q.set("limit", String(params.limit));
  return apiFetch(`/api/v1/finance/quotes?${q}`, { token });
}

export async function createQuote(token: string | null, data: {
  clientId: string; title: string;
  lineItems: Array<{ id: string; description: string; quantity: number; unitPrice: number; amount: number }>;
  taxRate?: number; discountType?: string; discountValue?: number;
  validUntil?: string; notes?: string; terms?: string;
}) {
  return apiFetch("/api/v1/finance/quotes", { token, method: "POST", body: JSON.stringify(data) });
}

export async function approveQuote(token: string | null, id: string, version: number) {
  return apiFetch(`/api/v1/finance/quotes/${id}/approve`, { token, method: "POST", body: JSON.stringify({ version }) });
}

export async function sendQuote(token: string | null, id: string, version: number) {
  return apiFetch(`/api/v1/finance/quotes/${id}/send`, { token, method: "POST", body: JSON.stringify({ version }) });
}

export async function acceptQuote(token: string | null, id: string, version: number, acceptedBy: string) {
  return apiFetch(`/api/v1/finance/quotes/${id}/accept`, { token, method: "POST", body: JSON.stringify({ version, acceptedBy }) });
}

export async function rejectQuote(token: string | null, id: string, version: number) {
  return apiFetch(`/api/v1/finance/quotes/${id}/reject`, { token, method: "POST", body: JSON.stringify({ version }) });
}

// Contracts
export async function listContracts(token: string | null, params?: { clientId?: string; status?: string }) {
  const q = new URLSearchParams();
  if (params?.clientId) q.set("clientId", params.clientId);
  if (params?.status) q.set("status", params.status);
  return apiFetch(`/api/v1/finance/contracts?${q}`, { token });
}

export async function createContract(token: string | null, data: {
  clientId: string; title: string; content: string; value: number;
  quoteId?: string; startDate?: string; endDate?: string; currency?: string;
}) {
  return apiFetch("/api/v1/finance/contracts", { token, method: "POST", body: JSON.stringify(data) });
}

export async function approveContract(token: string | null, id: string, version: number) {
  return apiFetch(`/api/v1/finance/contracts/${id}/approve`, { token, method: "POST", body: JSON.stringify({ version }) });
}

export async function signContract(token: string | null, id: string, version: number, signedBy: string) {
  return apiFetch(`/api/v1/finance/contracts/${id}/sign`, { token, method: "POST", body: JSON.stringify({ version, signedBy }) });
}

// Invoices
export async function listInvoices(token: string | null, params?: { clientId?: string; status?: string; search?: string }) {
  const q = new URLSearchParams();
  if (params?.clientId) q.set("clientId", params.clientId);
  if (params?.status) q.set("status", params.status);
  if (params?.search) q.set("search", params.search);
  return apiFetch(`/api/v1/finance/invoices?${q}`, { token });
}

export async function createInvoice(token: string | null, data: {
  clientId: string; title: string;
  lineItems: Array<{ id: string; description: string; quantity: number; unitPrice: number; amount: number }>;
  taxType?: string; cgstRate?: number; sgstRate?: number; igstRate?: number;
  dueDate?: string; notes?: string; quoteId?: string; contractId?: string;
}) {
  return apiFetch("/api/v1/finance/invoices", { token, method: "POST", body: JSON.stringify(data) });
}

export async function approveInvoice(token: string | null, id: string, version: number) {
  return apiFetch(`/api/v1/finance/invoices/${id}/approve`, { token, method: "POST", body: JSON.stringify({ version }) });
}

export async function issueInvoice(token: string | null, id: string, version: number) {
  return apiFetch(`/api/v1/finance/invoices/${id}/issue`, { token, method: "POST", body: JSON.stringify({ version }) });
}

export async function cancelInvoice(token: string | null, id: string, version: number) {
  return apiFetch(`/api/v1/finance/invoices/${id}/cancel`, { token, method: "POST", body: JSON.stringify({ version }) });
}

// Payments
export async function listPayments(token: string | null, params?: { clientId?: string; invoiceId?: string }) {
  const q = new URLSearchParams();
  if (params?.clientId) q.set("clientId", params.clientId);
  if (params?.invoiceId) q.set("invoiceId", params.invoiceId);
  return apiFetch(`/api/v1/finance/payments?${q}`, { token });
}

export async function recordPayment(token: string | null, data: {
  invoiceId: string; amount: number; paymentMethod: string; reference?: string; notes?: string; paidAt?: string;
}) {
  return apiFetch("/api/v1/finance/payments", { token, method: "POST", body: JSON.stringify(data) });
}

// Expenses
export async function listExpenses(token: string | null, params?: { category?: string; status?: string; since?: string }) {
  const q = new URLSearchParams();
  if (params?.category) q.set("category", params.category);
  if (params?.status) q.set("status", params.status);
  if (params?.since) q.set("since", params.since);
  return apiFetch(`/api/v1/finance/expenses?${q}`, { token });
}

export async function createExpense(token: string | null, data: {
  category: string; description: string; amount: number; expenseDate: string;
  vendor?: string; taxAmount?: number; notes?: string;
}) {
  return apiFetch("/api/v1/finance/expenses", { token, method: "POST", body: JSON.stringify(data) });
}

export async function approveExpense(token: string | null, id: string, version: number) {
  return apiFetch(`/api/v1/finance/expenses/${id}/approve`, { token, method: "POST", body: JSON.stringify({ version }) });
}

export async function rejectExpense(token: string | null, id: string, version: number) {
  return apiFetch(`/api/v1/finance/expenses/${id}/reject`, { token, method: "POST", body: JSON.stringify({ version }) });
}

export async function getExpenseSummary(token: string | null, since?: string) {
  const q = new URLSearchParams();
  if (since) q.set("since", since);
  return apiFetch(`/api/v1/finance/expenses/summary?${q}`, { token });
}

// Reports
export async function getProfitLoss(token: string | null, params?: { from?: string; to?: string }) {
  const q = new URLSearchParams();
  if (params?.from) q.set("from", params.from);
  if (params?.to) q.set("to", params.to);
  return apiFetch(`/api/v1/finance/reports/profit-loss?${q}`, { token });
}

export async function getFinanceSummary(token: string | null) {
  return apiFetch("/api/v1/finance/reports/summary", { token });
}
